<!-- src/components/SupportInfo.vue -->
<template>
  <div class="support-container min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 lg:p-8">
    <!-- Hero Section -->
    <header class="relative overflow-hidden rounded-[2.5rem] bg-white dark:bg-gray-900 p-8 lg:p-14 mb-10 shadow-xl border border-slate-200 dark:border-gray-800">
      <div class="absolute -top-40 -right-40 w-[32rem] h-[32rem] rounded-full pointer-events-none" style="background: radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%);"></div>
      <div class="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full pointer-events-none" style="background: radial-gradient(circle, rgba(37, 99, 235, 0.06) 0%, transparent 70%);"></div>

      <div class="absolute inset-0 opacity-[0.4] pointer-events-none dark:opacity-[0.05]" style="background-image: linear-gradient(rgba(100,116,139,.08) 1px, transparent 1px), linear-gradient(90deg, rgba(100,116,139,.08) 1px, transparent 1px); background-size: 40px 40px;"></div>

      <div class="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-10">
        <div class="text-center lg:text-left space-y-6 max-w-2xl">
          <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-widest">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            Centro de Soporte Oficial
          </div>

          <h1 class="text-4xl lg:text-6xl font-black text-slate-900 dark:text-white leading-[1.05] tracking-tight">
            ¿Cómo podemos
            <br class="hidden lg:block">
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500">ayudarte</span>
            <span class="text-slate-900 dark:text-white"> hoy?</span>
          </h1>

          <p class="text-slate-600 dark:text-gray-400 text-lg font-normal leading-relaxed">
            Guías paso a paso para que aproveches al máximo el sistema.
          </p>
        </div>

        <div class="hidden lg:flex items-center justify-center relative group">
          <div class="absolute inset-0 bg-blue-500/10 rounded-full blur-3xl group-hover:bg-blue-500/20 transition-all duration-700"></div>
          <div class="relative bg-slate-50 dark:bg-gray-800 rounded-3xl p-6 border border-slate-200 dark:border-gray-700 shadow-lg">
            <img
              :src="logoUrl"
              alt="SYSTEM JJ Logo"
              class="w-56 h-36 object-contain transform group-hover:scale-105 transition-transform duration-500"
              @error="handleImageError"
            >
          </div>
        </div>
      </div>
    </header>

    <!-- MÓDULOS DEL SISTEMA -->
    <section class="mb-10">
      <div class="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h2 class="text-xl font-black text-slate-800 dark:text-white flex items-center gap-3 uppercase tracking-wider">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <Icon icon="mdi:apps" class="text-white" />
          </div>
          Módulos del Sistema
        </h2>
        <span class="text-xs font-black text-slate-400 uppercase tracking-widest">
          5 módulos disponibles
        </span>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <div v-for="modulo in modulos" :key="modulo.nombre" class="module-card group">
          <div :class="['module-icon', 'bg-gradient-to-br', modulo.colorBg]">
            <Icon :icon="modulo.icon" class="text-white text-2xl" />
          </div>
          <h3 class="module-title">{{ modulo.nombre }}</h3>
          <p class="module-desc">{{ modulo.descripcion }}</p>
          <div class="module-features">
            <span v-for="(feat, i) in modulo.features" :key="i" class="feature-badge">
              <Icon icon="mdi:check" class="text-[8px]" />
              {{ feat }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <div class="grid lg:grid-cols-3 gap-8">
      <!-- Left Column -->
      <div class="lg:col-span-1 space-y-6">

        <!-- Canales de Atención -->
        <div>
          <h2 class="text-lg font-black text-slate-800 dark:text-white flex items-center gap-2 mb-4 uppercase tracking-wider">
            <Icon icon="mdi:phone-message" class="text-blue-600 text-xl" />
            Canales de Atención
          </h2>

          <div class="grid gap-3">
            <a href="https://wa.me/584167594723?text=Hola,%20necesito%20soporte%20técnico" target="_blank" rel="noopener noreferrer" class="contact-card group">
              <div class="card-icon bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-lg shadow-emerald-500/30">
                <Icon icon="mdi:whatsapp" />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="font-bold text-slate-800 dark:text-white text-sm">WhatsApp Soporte</h3>
                <p class="text-xs text-slate-500 dark:text-gray-400 truncate">+58 416-759-4723</p>
              </div>
              <Icon icon="mdi:chevron-right" class="text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
            </a>

            <a href="https://wa.me/584145572936?text=Hola,%20necesito%20soporte%20técnico" target="_blank" rel="noopener noreferrer" class="contact-card group">
              <div class="card-icon bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-lg shadow-emerald-500/30">
                <Icon icon="mdi:whatsapp" />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="font-bold text-slate-800 dark:text-white text-sm">WhatsApp Soporte</h3>
                <p class="text-xs text-slate-500 dark:text-gray-400 truncate">+58 414-557-2936</p>
              </div>
              <Icon icon="mdi:chevron-right" class="text-slate-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all" />
            </a>

            <a href="mailto:systemjj.soporte@gmail.com" class="contact-card group">
              <div class="card-icon bg-gradient-to-br from-red-400 to-red-600 text-white shadow-lg shadow-red-500/30">
                <Icon icon="mdi:gmail" />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="font-bold text-slate-800 dark:text-white text-sm">Correo Electrónico</h3>
                <p class="text-xs text-slate-500 dark:text-gray-400 truncate">systemjj.soporte@gmail.com</p>
              </div>
              <Icon icon="mdi:chevron-right" class="text-slate-400 group-hover:text-red-500 group-hover:translate-x-1 transition-all" />
            </a>

            <a href="https://www.sistemas-jj.com" target="_blank" rel="noopener noreferrer" class="contact-card group">
              <div class="card-icon bg-gradient-to-br from-indigo-400 to-indigo-600 text-white shadow-lg shadow-indigo-500/30">
                <Icon icon="mdi:web" />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="font-bold text-slate-800 dark:text-white text-sm">Sitio Web Oficial</h3>
                <p class="text-xs text-slate-500 dark:text-gray-400 truncate">www.sistemas-jj.com</p>
              </div>
              <Icon icon="mdi:chevron-right" class="text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
            </a>
          </div>
        </div>

        <!-- Redes Sociales -->
        <div>
          <h2 class="text-lg font-black text-slate-800 dark:text-white flex items-center gap-2 mb-4 uppercase tracking-wider">
            <Icon icon="mdi:share-variant" class="text-blue-600 text-xl" />
            Síguenos
          </h2>

          <div class="grid grid-cols-2 gap-3">
            <a :href="instagramUrl" target="_blank" rel="noopener noreferrer" class="social-card group" title="Síguenos en Instagram">
              <div class="social-icon bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 shadow-lg shadow-pink-500/30">
                <Icon icon="mdi:instagram" />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="font-bold text-slate-800 dark:text-white text-xs uppercase tracking-wider">Instagram</h3>
                <p class="text-[10px] text-slate-500 dark:text-gray-400 truncate">@systemsjj</p>
              </div>
            </a>

            <a :href="facebookUrl" target="_blank" rel="noopener noreferrer" class="social-card group" title="Síguenos en Facebook">
              <div class="social-icon bg-gradient-to-br from-blue-600 to-blue-800 shadow-lg shadow-blue-600/30">
                <Icon icon="mdi:facebook" />
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="font-bold text-slate-800 dark:text-white text-xs uppercase tracking-wider">Facebook</h3>
                <p class="text-[10px] text-slate-500 dark:text-gray-400 truncate">System JJ</p>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Right Column: GUÍAS -->
      <div class="lg:col-span-2 space-y-6">
        <section class="space-y-6">
          <div class="flex items-center justify-between flex-wrap gap-3">
            <h2 class="text-xl font-black text-slate-800 dark:text-white flex items-center gap-3 uppercase tracking-wider">
              <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Icon icon="mdi:book-open-page-variant" class="text-white" />
              </div>
              Guías de Registro
            </h2>
            <span class="text-xs font-black text-slate-400 uppercase tracking-widest">
              {{ guias.length }} guías
            </span>
          </div>

          <div class="space-y-4">
            <div v-for="(guia, i) in guias" :key="i" class="guide-card group">
              <button
                @click="toggleGuia(i)"
                class="w-full flex items-center justify-between p-5 text-left focus:outline-none"
              >
                <div class="flex items-center gap-4 flex-1 min-w-0">
                  <div :class="['guide-icon', 'bg-gradient-to-br', guia.colorBg]">
                    <Icon :icon="guia.icon" class="text-white text-xl" />
                  </div>
                  <div class="min-w-0">
                    <h3 class="font-black text-slate-800 dark:text-white text-base truncate">{{ guia.titulo }}</h3>
                    <p class="text-xs text-slate-500 dark:text-gray-400 truncate">{{ guia.subtitulo }}</p>
                  </div>
                </div>
                <div :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all',
                  activeGuia === i ? 'bg-blue-600 text-white rotate-180' : 'bg-slate-100 dark:bg-gray-800 text-slate-400'
                ]">
                  <Icon icon="mdi:chevron-down" class="text-lg" />
                </div>
              </button>

              <div
                class="overflow-hidden transition-all duration-500 ease-in-out"
                :style="{ maxHeight: activeGuia === i ? '2000px' : '0' }"
              >
                <div class="px-5 pb-5 pt-2 border-t border-slate-100 dark:border-gray-800">
                  <div class="flex flex-wrap gap-3 mb-4">
                    <div class="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-gray-400 bg-slate-100 dark:bg-gray-800 px-3 py-1.5 rounded-lg">
                      <Icon icon="mdi:route" class="text-sm" />
                      Ruta: <code class="text-blue-600 dark:text-blue-400 font-mono">{{ guia.ruta }}</code>
                    </div>
                    <div class="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-gray-400 bg-slate-100 dark:bg-gray-800 px-3 py-1.5 rounded-lg">
                      <Icon icon="mdi:clock-outline" class="text-sm" />
                      {{ guia.tiempo }}
                    </div>
                  </div>

                  <div class="space-y-3">
                    <div v-for="(paso, j) in guia.pasos" :key="j" class="flex gap-3 items-start">
                      <div class="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <span class="text-xs font-black text-blue-600 dark:text-blue-400">{{ j + 1 }}</span>
                      </div>
                      <div class="flex-1 pt-0.5">
                        <p class="text-sm text-slate-700 dark:text-gray-300 leading-relaxed">
                          <span class="font-bold text-slate-900 dark:text-white">{{ paso.titulo }}</span>
                          <span v-if="paso.detalle">: {{ paso.detalle }}</span>
                        </p>
                        <p v-if="paso.tip" class="mt-1.5 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1.5">
                          <Icon icon="mdi:lightbulb-on-outline" class="text-sm" />
                          {{ paso.tip }}
                        </p>
                        <p v-if="paso.warning" class="mt-1.5 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1.5">
                          <Icon icon="mdi:alert-outline" class="text-sm" />
                          {{ paso.warning }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div v-if="guia.accion" class="mt-5 pt-4 border-t border-slate-100 dark:border-gray-800">
                    <router-link
                      :to="guia.accion.ruta"
                      class="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-xl font-black text-xs uppercase tracking-wider shadow-md shadow-blue-500/30 hover:shadow-lg transition-all"
                    >
                      <Icon :icon="guia.accion.icon" class="text-base" />
                      {{ guia.accion.texto }}
                      <Icon icon="mdi:arrow-right" class="text-sm" />
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Footer Branding -->
    <footer class="mt-16 text-center py-10 border-t border-slate-200 dark:border-gray-800">
      <div class="inline-flex items-center gap-4 p-5 rounded-3xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 shadow-lg hover:shadow-xl transition-all">
        <img :src="logoUrl" alt="SYSTEM JJ Logo" class="w-14 h-14 object-contain opacity-60 hover:opacity-100 hover:grayscale-0 grayscale transition-all cursor-pointer">
        <div class="text-left">
          <p class="text-[10px] font-black text-slate-500 dark:text-gray-500 uppercase tracking-widest">Desarrollado con ❤️ por</p>
          <a href="https://www.sistemas-jj.com" target="_blank" rel="noopener noreferrer"
            class="text-base font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600">
            SYSTEM JJ
          </a>
        </div>
      </div>

      <div class="mt-6 flex items-center justify-center gap-3">
        <a :href="instagramUrl" target="_blank" rel="noopener noreferrer"
          class="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 flex items-center justify-center text-white shadow-lg shadow-pink-500/30 hover:scale-110 transition-transform"
          title="Instagram">
          <Icon icon="mdi:instagram" class="text-xl" />
        </a>
        <a :href="facebookUrl" target="_blank" rel="noopener noreferrer"
          class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white shadow-lg shadow-blue-600/30 hover:scale-110 transition-transform"
          title="Facebook">
          <Icon icon="mdi:facebook" class="text-xl" />
        </a>
        <a href="mailto:systemjj.soporte@gmail.com"
          class="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white shadow-lg shadow-red-500/30 hover:scale-110 transition-transform"
          title="Email">
          <Icon icon="mdi:gmail" class="text-xl" />
        </a>
        <a href="https://www.sistemas-jj.com" target="_blank" rel="noopener noreferrer"
          class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/30 hover:scale-110 transition-transform"
          title="Sitio Web">
          <Icon icon="mdi:web" class="text-xl" />
        </a>
      </div>

      <p class="mt-6 text-xs text-slate-400 dark:text-gray-600 font-medium">
        © {{ currentYear }} Todos los derechos reservados · Sistema de Gestión <span class="font-black text-blue-600">SYSTEM JJ</span>
      </p>
    </footer>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";

export default {
  name: 'SupportInfo',
  components: { Icon },
  data() {
    return {
      logoUrl: require('@/assets/logo/jj.png'),
      imageError: false,
      activeGuia: 0,

      instagramUrl: 'https://www.instagram.com/systemsjj?stkn=bHhkZ2N2MmozcHVy',
      facebookUrl: 'https://www.facebook.com/people/System-JJ/61594524715644/',

      // MÓDULOS DEL SISTEMA
      modulos: [
        {
          nombre: 'Empresarial',
          descripcion: 'Gestión multi-tenant de empresas y licencias',
          icon: 'mdi:domain',
          color: 'blue',
          colorBg: 'from-blue-500 to-indigo-600',
          features: ['Multi-Tenant', 'Licencias', 'Config']
        },
        {
          nombre: 'Gimnasio',
          descripcion: 'Control de socios, membresías y accesos',
          icon: 'mdi:dumbbell',
          color: 'emerald',
          colorBg: 'from-emerald-500 to-teal-600',
          features: ['Membresías', 'Accesos', 'Torniquete']
        },
        {
          nombre: 'Streaming',
          descripcion: 'Plataforma de streaming y contenido digital',
          icon: 'mdi:play-circle',
          color: 'purple',
          colorBg: 'from-purple-500 to-pink-600',
          features: ['VOD', 'Suscripciones', 'Playlists']
        },
        {
          nombre: 'Restaurante',
          descripcion: 'Pedidos, mesas y facturación de restaurante',
          icon: 'mdi:silverware-fork-knife',
          color: 'orange',
          colorBg: 'from-orange-500 to-red-600',
          features: ['Mesas', 'Comandas', 'Delivery']
        },
        {
          nombre: 'A Medida',
          descripcion: 'Sistema modular y personalizable por empresa',
          icon: 'mdi:view-dashboard-variant',
          color: 'cyan',
          colorBg: 'from-cyan-500 to-blue-600',
          features: ['Modular', 'Personalizable', 'Escalable']
        }
      ],

      // GUÍAS PASO A PASO
      guias: [
        {
          titulo: 'Cómo registrar un Cliente',
          subtitulo: 'Aprende a crear clientes para ventas y servicios',
          icon: 'mdi:account-plus',
          color: 'blue',
          colorBg: 'from-blue-500 to-indigo-600',
          ruta: 'Ventas → Clientes',
          tiempo: '~1 minuto',
          pasos: [
            { titulo: 'Ve al módulo de Clientes', detalle: 'En el menú lateral, busca "Gestión Ventas" → "Clientes"' },
            { titulo: 'Haz clic en el botón "Nuevo Cliente"', detalle: 'Está en la esquina superior derecha con un ícono de +' },
            { titulo: 'Completa los datos personales', detalle: 'Tipo de documento, número de documento, nombre y apellido', tip: 'El sistema formatea automáticamente la cédula al formato V-12345678' },
            { titulo: 'Agrega la información de contacto', detalle: 'Teléfono (formato 0416-7594723) y correo electrónico', warning: 'El teléfono es muy importante para enviar mensajes por WhatsApp' },
            { titulo: 'Opcional: agrega la dirección', detalle: 'Dirección completa del cliente para envíos o servicios a domicilio' },
            { titulo: 'Verifica el estado', detalle: 'Selecciona "Activo" para que el cliente esté disponible en ventas', tip: 'Si lo pones como Inactivo, no aparecerá en el buscador de ventas' },
            { titulo: 'Haz clic en "Crear Cliente"', detalle: 'El sistema guardará el cliente y aparecerá en la lista' }
          ],
          accion: { texto: 'Ir a Clientes', ruta: '/clientes', icon: 'mdi:account-group' }
        },
        {
          titulo: 'Cómo registrar una Categoría',
          subtitulo: 'Organiza tus productos por categorías',
          icon: 'mdi:folder-plus',
          color: 'purple',
          colorBg: 'from-purple-500 to-indigo-600',
          ruta: 'Inventario → Categorías',
          tiempo: '~1 minuto',
          pasos: [
            { titulo: 'Ve al módulo de Categorías', detalle: 'En el menú lateral, busca "Gestión Inventario" → "Categorías"' },
            { titulo: 'Haz clic en "Nueva Categoría"', detalle: 'Está en la esquina superior derecha' },
            { titulo: 'Escribe el nombre de la categoría', detalle: 'Ejemplos: "Computadoras", "Accesorios", "Software"', warning: 'El nombre debe ser único' },
            { titulo: 'Agrega una descripción (opcional)', detalle: 'Máximo 500 caracteres. Aparece en reportes' },
            { titulo: 'Define el margen de ganancia', detalle: 'Porcentaje por defecto para los productos', tip: 'Recomendado: 30% general, 40% accesorios' },
            { titulo: 'Selecciona el estado', detalle: 'Activa (disponible) o Inactiva (oculta)' },
            { titulo: 'Haz clic en "Crear"', detalle: 'La categoría aparecerá en la lista' }
          ],
          accion: { texto: 'Ir a Categorías', ruta: '/categorias', icon: 'mdi:folder-multiple' }
        },
        {
          titulo: 'Cómo registrar un Proveedor',
          subtitulo: 'Administra tus proveedores de productos',
          icon: 'mdi:truck-plus',
          color: 'emerald',
          colorBg: 'from-emerald-500 to-teal-600',
          ruta: 'Compras → Proveedores',
          tiempo: '~2 minutos',
          pasos: [
            { titulo: 'Ve al módulo de Proveedores', detalle: 'En el menú lateral, busca "Gestión Compras" → "Proveedores"' },
            { titulo: 'Haz clic en "Nuevo Proveedor"', detalle: 'Está en la esquina superior derecha' },
            { titulo: 'Completa los datos de la empresa', detalle: 'Nombre comercial y RIF', warning: 'El RIF debe ser único' },
            { titulo: 'Selecciona el tipo de proveedor', detalle: 'Mayorista, Distribuidor o Fabricante' },
            { titulo: 'Agrega el contacto del asesor', detalle: 'Nombre de la persona que te atiende' },
            { titulo: 'Agrega teléfono y email', detalle: 'Datos de contacto para pedidos' },
            { titulo: 'Selecciona el estado', detalle: 'Activo para poder registrarle compras' },
            { titulo: 'Haz clic en "Crear"', detalle: 'El proveedor aparecerá en la lista' }
          ],
          accion: { texto: 'Ir a Proveedores', ruta: '/proveedores', icon: 'mdi:truck-delivery' }
        },
        {
          titulo: 'Cómo registrar un Producto',
          subtitulo: 'Agrega productos al inventario con precios y stock',
          icon: 'mdi:package-variant-plus',
          color: 'orange',
          colorBg: 'from-orange-500 to-amber-600',
          ruta: 'Inventario → Productos',
          tiempo: '~2 minutos',
          pasos: [
            { titulo: 'Ve al módulo de Productos', detalle: 'En el menú lateral, busca "Gestión Inventario" → "Productos"' },
            { titulo: 'Haz clic en "Nuevo"', detalle: 'También puedes escanear código y crear desde ahí', tip: 'Escanea el código de barras para autocompletar' },
            { titulo: 'Completa "Información Básica"', detalle: 'Código único, código de barras, nombre, categoría y proveedor', warning: 'El código debe ser único' },
            { titulo: 'Agrega los detalles', detalle: 'Marca, modelo, descripción y foto' },
            { titulo: 'Configura el "Precio de Compra"', detalle: 'Costo real del producto en USD (USDT)', warning: 'Este valor es la base para el precio de venta' },
            { titulo: 'Define el margen de ganancia', detalle: 'Porcentaje o Monto Fijo', tip: 'Ej: costo $100 + 30% = precio venta $130' },
            { titulo: 'Revisa el "Precio de Venta Sugerido"', detalle: 'Se calcula automáticamente' },
            { titulo: 'Agrega "Stock" y "Garantía"', detalle: 'Stock actual, mínimo, máximo y meses de garantía' },
            { titulo: 'Selecciona estado y guarda', detalle: 'Elige "Activo" y haz clic en "Crear"' }
          ],
          accion: { texto: 'Ir a Productos', ruta: '/productos', icon: 'mdi:package-variant' }
        },
        {
          titulo: 'Cómo registrar una Venta',
          subtitulo: 'Aprende a facturar productos al contado o crédito',
          icon: 'mdi:cart-plus',
          color: 'green',
          colorBg: 'from-emerald-500 to-teal-600',
          ruta: 'Ventas → Nueva Venta',
          tiempo: '~3 minutos',
          pasos: [
            { titulo: 'Ve al módulo de Ventas', detalle: 'En el menú lateral, busca "Gestión Ventas" → "Nueva Venta"' },
            { titulo: 'Selecciona el tipo de venta', detalle: 'Contado o Crédito', tip: 'Las ventas a crédito tienen opciones de financiamiento' },
            { titulo: 'Selecciona el cliente', detalle: 'Busca por nombre, documento o teléfono', warning: 'Sin cliente no puedes agregar productos' },
            { titulo: 'Agrega los productos', detalle: 'Escanea el código o busca manualmente', tip: 'El lector de código es lo más rápido' },
            { titulo: 'Ajusta cantidad y precio', detalle: 'Modifica cantidad, precio unitario y garantía' },
            { titulo: 'Aplica descuentos si es necesario', detalle: 'En el resumen puedes agregar descuento en USD' },
            { titulo: 'Selecciona el método de pago', detalle: 'Efectivo, Pago Móvil, Transferencia o Punto de Venta' },
            { titulo: 'Verifica la tasa de cambio', detalle: 'El sistema aplica la tasa de venta actual' },
            { titulo: 'Haz clic en "Crear Venta"', detalle: 'La factura se generará automáticamente', tip: 'Puedes elegir "Ver ventas" o "Permanecer"' }
          ],
          accion: { texto: 'Ir a Nueva Venta', ruta: '/ventas/nueva', icon: 'mdi:cart' }
        }
      ]
    };
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    }
  },
  methods: {
    handleImageError() {
      this.imageError = true;
      console.warn('Error al cargar el logo. Verifica la ruta: @/assets/logo/jj.png');
    },
    toggleGuia(index) {
      this.activeGuia = this.activeGuia === index ? null : index;
    }
  }
};
</script>

<style scoped>
.support-container {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

.module-card {
  @apply bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-2xl p-4 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer flex flex-col items-center text-center;
}

.module-icon {
  @apply w-14 h-14 rounded-2xl flex items-center justify-center mb-3 shadow-lg;
}

.module-title {
  @apply font-black text-slate-800 dark:text-white text-sm uppercase tracking-wider mb-1;
}

.module-desc {
  @apply text-[11px] text-slate-500 dark:text-gray-400 leading-tight mb-3 min-h-[28px];
}

.module-features {
  @apply flex flex-wrap justify-center gap-1;
}

.feature-badge {
  @apply inline-flex items-center gap-0.5 text-[9px] font-bold text-slate-600 dark:text-gray-400 bg-slate-100 dark:bg-gray-800 px-2 py-0.5 rounded-full;
}

.contact-card {
  @apply flex items-center gap-4 p-4 rounded-2xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 shadow-sm hover:shadow-xl hover:shadow-blue-500/10 transition-all transform hover:-translate-y-1 cursor-pointer;
}

.card-icon {
  @apply w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0;
}

.social-card {
  @apply flex items-center gap-3 p-3 rounded-2xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer;
}

.social-icon {
  @apply w-10 h-10 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0;
}

.guide-card {
  @apply bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-2xl overflow-hidden transition-all;
}

.guide-card:hover {
  @apply border-blue-300 dark:border-blue-700 shadow-lg;
}

.guide-icon {
  @apply w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg;
}

::-webkit-scrollbar {
  width: 8px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}
.dark ::-webkit-scrollbar-thumb {
  background: #334155;
}
</style>